import { NgModule } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ScrollStrategyOptions } from '@angular/cdk/overlay';
import { PortalModule } from '@angular/cdk/portal';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { MatNativeDateModule } from '@angular/material/core';
import { MAT_AUTOCOMPLETE_SCROLL_STRATEGY, MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DIALOG_DEFAULT_OPTIONS, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule, MatIconRegistry } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MAT_PAGINATOR_DEFAULT_OPTIONS, MatPaginatorIntl, MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MAT_SELECT_CONFIG, MatSelectModule } from '@angular/material/select';
import { MAT_SNACK_BAR_DEFAULT_OPTIONS, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MAT_TOOLTIP_DEFAULT_OPTIONS, MatTooltipModule } from '@angular/material/tooltip';

import { NgbModule } from '@ng-bootstrap/ng-bootstrap';



export const PAGINATOR_INTL_FACTORY = (): MatPaginatorIntl => {
  const paginatorIntl = new MatPaginatorIntl();

  paginatorIntl.itemsPerPageLabel = $localize`:@@global.label.itemsPerPage:Per page`;
  paginatorIntl.firstPageLabel = $localize`:@@global.label.firstPage:First Page`;
  paginatorIntl.lastPageLabel = $localize`:@@global.label.lastPage:Last Page`;
  paginatorIntl.previousPageLabel = $localize`:@@global.label.prevPage:Previous Page`;
  paginatorIntl.nextPageLabel = $localize`:@@global.label.nextPage:Next Page`;
  paginatorIntl.getRangeLabel = (page: number, pageSize: number, length: number) => {
    if (length === 0 || pageSize === 0) {
      return `0 ${$localize`:@@global.label.range:of`} ${length}`;
    }
    length = Math.max(length, 0);
    const startIndex = page * pageSize;
    const endIndex =
      startIndex < length ? Math.min(startIndex + pageSize, length) : startIndex + pageSize;
    return `${startIndex + 1} – ${endIndex} ` + $localize`:@@global.label.range:of` + ` ${length}`;
  };

  return paginatorIntl;
};

@NgModule({
  imports: [
    PortalModule,
    ScrollingModule,
    MatNativeDateModule,
    MatAutocompleteModule,
    MatButtonModule,
    MatCardModule,
    MatCheckboxModule,
    MatChipsModule,
    MatDatepickerModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatMenuModule,
    MatPaginatorModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatSnackBarModule,
    MatSortModule,
    MatTableModule,
    MatTabsModule,
    MatTooltipModule,
    NgbModule,
  ],
  exports: [
    PortalModule,
    ScrollingModule,
    MatNativeDateModule,
    MatAutocompleteModule,
    MatButtonModule,
    MatCardModule,
    MatCheckboxModule,
    MatChipsModule,
    MatDatepickerModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatMenuModule,
    MatPaginatorModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatSnackBarModule,
    MatSortModule,
    MatTableModule,
    MatTabsModule,
    MatTooltipModule,
    NgbModule,
  ],
  providers: [{
    provide: MAT_AUTOCOMPLETE_SCROLL_STRATEGY,
    useFactory: (scrollStrategyOptions: ScrollStrategyOptions) => {
      return scrollStrategyOptions.reposition;
    },
    deps: [ScrollStrategyOptions]
  }, {
    provide: MAT_DIALOG_DEFAULT_OPTIONS,
    useValue: {
      autoFocus: false,
      closeOnNavigation: true,
      disableClose: true,
      hasBackdrop: true,
      restoreFocus: false,
      width: '680px',
      minWidth: '320px',
      maxWidth: '80vw'
    }
  }, {
    provide: MAT_PAGINATOR_DEFAULT_OPTIONS,
    useValue: {
      formFieldAppearance: 'outline',
      pageSizeOptions: [20, 50, 100],
      pageSize: 20,
      showFirstLastButtons: true
    }
  }, {
    provide: MatPaginatorIntl,
    useFactory: PAGINATOR_INTL_FACTORY
  }, {
    provide: MAT_SNACK_BAR_DEFAULT_OPTIONS,
    useValue: {
      duration: 5000,
      horizontalPosition: 'center',
      verticalPosition: 'top'
    }
  }, {
    provide: MAT_SELECT_CONFIG,
    useValue: {
      disableOptionCentering: false
    }
  }, {
    provide: MAT_TOOLTIP_DEFAULT_OPTIONS,
    useValue: {
      showDelay: 0,
      hideDelay: 0,
      touchendHideDelay: 1500,
      position: 'above'
    }
  }]
})
export class SharedLibsModule {
  constructor(private domSanitizer: DomSanitizer, private iconRegistry: MatIconRegistry) {
    iconRegistry.setDefaultFontSetClass('material-symbols');
    iconRegistry.registerFontClassAlias('fill', 'material-symbols-fill');
    iconRegistry.registerFontClassAlias('rounded', 'material-symbols');
    iconRegistry.registerFontClassAlias('design', 'design-icon');
    this.addIcons();
  }

  private addIcons() {
    this.iconRegistry.addSvgIconInNamespace('twe', 'drafts', this.getSecurityIcon('drafts'));
    this.iconRegistry.addSvgIconInNamespace('twe', 'person_off', this.getSecurityIcon('person-off'));
    this.iconRegistry.addSvgIconInNamespace('twe', 'empty', this.getSecurityIcon('empty'));
    this.iconRegistry.addSvgIconInNamespace('twe', 'hourglass_empty', this.getSecurityIcon('hourglass-empty'));
  }

  private getSecurityIcon(icon: string): SafeResourceUrl {
    return this.domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/icon-' + icon + '.svg');
  }
}
